/**
 * Las reglas de un tutor que el servidor también comprueba.
 *
 * Están aquí, como funciones puras, y no dentro del componente por dos motivos:
 * se pueden probar sin pintar nada, y la próxima pantalla que toque tutores —la
 * app de familias, cuando llegue— las tiene a mano en vez de reimplementarlas.
 *
 * **Y no sustituyen al servidor.** Si esto se saltara, la API responde `422`
 * igual. Lo que evita es ofrecer una combinación que se sabe que va a fallar, que
 * es lo que convierte una regla de negocio en un error incomprensible.
 */

export type Acceso = 'full' | 'basic';

export interface OpcionesDeTutor {
  readonly access_level: Acceso;
  readonly receives_billing: boolean;
  readonly is_primary: boolean;
  readonly can_pickup: boolean;
}

/**
 * Modo abuelo y facturación son **incompatibles**.
 *
 * `BFF_BO.md` §7.1: «el modo abuelo existe para seguir el progreso sin ver el
 * dinero, así que dirigirle el recibo produce una factura que esa persona no
 * puede abrir». El servidor responde `422` con el campo señalado.
 */
export const puedeRecibirFacturacion = (acceso: Acceso): boolean =>
  acceso !== 'basic';

/**
 * Ajusta las opciones tras un cambio, para que nunca queden en un estado que el
 * servidor vaya a rechazar.
 *
 * Elegir «solo seguimiento» apaga la facturación en el acto. Es deliberado que
 * apague en silencio en vez de avisar: la casilla queda deshabilitada **con la
 * explicación al lado**, así que el motivo se lee sin necesidad de un aviso.
 */
export function ajustar<T extends OpcionesDeTutor>(opciones: T): T {
  if (!puedeRecibirFacturacion(opciones.access_level)) {
    return { ...opciones, receives_billing: false };
  }
  return opciones;
}

/** En España, y para todo lo que aquí importa. */
export const MAYORIA_DE_EDAD = 18;

/**
 * Solo un alumno mayor de edad puede ser su propio tutor · 13-10-2026
 *
 * Y con la fecha de nacimiento puesta: sin ella no se sabe, y el servidor responde
 * `422 self_guardian_needs_adult`. La opción ni se ofrece.
 */
export const puedeSerSuPropioTutor = (edad: number | null): boolean =>
  edad !== null && edad >= MAYORIA_DE_EDAD;

/**
 * El propio alumno, con acceso completo y sin «puede recoger».
 *
 * El acceso básico es para seguir a otra persona sin ver su dinero ni su salud, y de
 * uno mismo no protege nada (el servidor lo rechaza). Y «recogerse» a uno mismo no
 * significa nada.
 */
export function ajustarAlPropio<
  T extends OpcionesDeTutor & { relationship: string },
>(opciones: T): T {
  if (opciones.relationship !== 'propio') return opciones;
  return { ...opciones, access_level: 'full', can_pickup: false };
}
